import { SetStateAction } from "react";
import { GroupOrOUList, IGroupMappings } from "./GoogleAuthenticationTab";
import { IOption } from "../../../components/utilities/MultiSelect";
import styles from "./PermissionGroupSection.module.scss";
import { SearchableGroupSelect } from "./SearchableGroupSelect";

export const permissionGroupLabels = {
  adminGroup: "School Blocks Admins",
  staffGroup: "Staff",
  teacherGroup: "Teachers",
  studentGroup: "Students",
  guardianGroup: "Guardians",
};

type PermissionGroupSectionProps = {
  organization: OrganizationTypeOrganization;
  groupOuReference?: GroupOrOUList;
  groupMappings: IGroupMappings;
  setGroupMappings: SetStateAction<any>;
  setHasChanged: SetStateAction<any>;
  useSingleSelect?: boolean; // used for departments/groups where we only allow one associated group/OU
} & (
  | {
      provider: "google";
      useGroupSearch: true;
      identityOUList: null;
      identityGroupsList: null;
    }
  | {
      provider: "google";
      useGroupSearch: false;
      identityOUList: GroupOrOUList;
      identityGroupsList: GroupOrOUList;
    }
  | {
      provider: "office365";
      useGroupSearch: true;
      identityGroupsList: null;
    }
  | {
      provider: "office365";
      useGroupSearch: false;
      identityGroupsList: GroupOrOUList;
    }
);

type PermissionGroupProps = PermissionGroupSectionProps & {
  groupLabel: string;
};

export function PermissionGroup(props: PermissionGroupProps) {
  let groupId = props.groupMappings[props.organization.id]
    ? props.groupMappings[props.organization.id]?.[props.groupLabel]
    : [];

  // if using "old" style, convert it to new one. this should not be removed.
  if (!Array.isArray(groupId)) {
    groupId = [groupId];
  }

  let googleOptions: { [value: string]: IOption[] } = {};
  let microsoftOptions: { [value: string]: IOption[] } = {};

  if (props.provider === "google" && !props.useGroupSearch) {
    const googleGroupsList = props.identityGroupsList
      ? Object.entries(props.identityGroupsList).map(([id, title]) => ({
          value: id,
          title,
        }))
      : [];
    const googleOUList = props.identityOUList
      ? Object.entries(props.identityOUList).map(([structuredName, label]) => {
          const depth = (structuredName.match(/\//g) || []).length - 1;
          return {
            value: structuredName,
            title: " ".repeat(depth * 2) + label,
          };
        })
      : [];

    Object.assign(googleOptions, {
      "GROUPS:": googleGroupsList,
      "ORGANIZATIONAL UNITS:": googleOUList,
    });

    if (props.groupLabel === "studentGroup") {
      delete googleOptions["GROUPS:"];
    }
  }

  // Only flag "broken" ids when we actually have the whole directory to check
  // against. In search mode the backend can't send the full lists (it sends an
  // empty {} which is truthy), so we can't determine broken-ness — skip it.
  const broken_groups_and_ous = !props.useGroupSearch
    ? groupId
        .map((id) => {
          if (props.provider === "google") {
            if (
              !props.identityGroupsList?.[id] &&
              !props.identityOUList?.[id]
            ) {
              return { value: id, title: id };
            }
          } else if (!props.identityGroupsList?.[id]) {
            return { value: id, title: id };
          }
          return null; // In case the group or OU is found, return null to filter out later
        })
        .filter((item) => item !== null) // Filter out null values
    : [];

  if (broken_groups_and_ous.length > 0) {
    if (props.provider === "google") {
      googleOptions["BROKEN GROUPS AND OUS:"] = broken_groups_and_ous;
    } else {
      microsoftOptions["BROKEN GROUPS:"] = broken_groups_and_ous;
    }
  }
  if (props.provider !== "google" && props.identityGroupsList) {
    microsoftOptions["GROUPS:"] = Object.entries(props.identityGroupsList).map(
      ([id, title]) => ({
        value: id,
        title,
      }),
    );
  }

  function setGroupId(value) {
    props.setGroupMappings((prev) => ({
      ...prev,
      [props.organization.id]: {
        ...prev[props.organization.id],
        [props.groupLabel]: value,
      },
    }));
    props.setHasChanged(true);
  }

  if (props.organization.oneroster_id && props.groupLabel !== "adminGroup") {
    return <div className={styles.sisSync}>SIS Roster Sync</div>;
  }

  const options =
    props.provider === "google" ? googleOptions : microsoftOptions;

  // Resolve a display label for each selected id from whatever directory data
  // is available, so names persist whether or not the search UI is active.
  const labels: { [id: string]: string } = {};
  groupId.forEach((id: string) => {
    const name =
      props.groupOuReference?.[id] ||
      props.identityGroupsList?.[id] ||
      (props.provider === "google" ? props.identityOUList?.[id] : undefined);
    if (name) labels[id] = name;
  });

  return (
    <SearchableGroupSelect
      multiple={!props.useSingleSelect}
      search={!!props.useGroupSearch}
      selected={groupId}
      onChange={(ids) =>
        setGroupId(
          props.useSingleSelect ? (ids.length ? ids[ids.length - 1] : []) : ids,
        )
      }
      options={options}
      labels={labels}
      brokenIds={broken_groups_and_ous.map((b) => b!.value)}
    />
  );
}

export function PermissionGroupSection(props: PermissionGroupSectionProps) {
  return (
    <tr>
      <th scope={"row"}>
        {props.organization.title}
        {props.organization.type === "district" ? " (and all schools)" : ""}
      </th>
      <td style={{ textAlign: "right" }}>
        {Object.entries(permissionGroupLabels)
          .filter(([key]) => {
            if (key === "guardianGroup") {
              return props.organization.oneroster_id;
            } else if (props.organization.type === "school") {
              return [
                "adminGroup",
                "staffGroup",
                "teacherGroup",
                "studentGroup",
              ].includes(key);
            } else {
              return ["adminGroup", "staffGroup"].includes(key);
            }
          })
          .map(([key, value]) => {
            return (
              <div key={key} style={{ marginBottom: "0.5rem" }}>
                <div style={{ marginBottom: "0.25rem" }}>{value}:</div>
                <PermissionGroup groupLabel={key} {...props} />
              </div>
            );
          })}
      </td>
    </tr>
  );
}
